import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import BigButton from '../components/BigButton'
import { api } from '../lib/api'
import { longDate, nlDatum } from '../lib/format'
import type { ArchivedEdition, Season } from '../lib/types'

type LoadResp = {
  history?: ArchivedEdition[]
  season?: Season
}

// Het "aparte linkje" op de persoonlijke pagina: wat je in een vorige editie
// koos. Zelfde token, geen extra login — de bewaarde momentopname per editie.
export default function VorigeEditie() {
  const { token = '' } = useParams()
  // `v` bewijst dat deze pagina bereikt is via de gemailde link — zonder dat
  // bewijs koppelt GET /api/me/:token geen archief op e-mail (zie
  // signToken() in server/util.ts), dus MijnPlek.tsx's link hierheen moet
  // 'm doorgeven, en deze pagina moet 'm op zijn beurt meesturen.
  const [searchParams] = useSearchParams()
  const verify = searchParams.get('v') ?? ''
  const [loading, setLoading] = useState(true)
  const [history, setHistory] = useState<ArchivedEdition[]>([])
  const [error, setError] = useState(false)

  useEffect(() => {
    let alive = true
    const qs = verify ? `?v=${encodeURIComponent(verify)}` : ''
    api<LoadResp>(`/api/me/${encodeURIComponent(token)}${qs}`).then((res) => {
      if (!alive) return
      if (res.ok) setHistory(res.data.history ?? [])
      else setError(true)
      setLoading(false)
    })
    return () => { alive = false }
  }, [token, verify])

  if (loading) return <div className="container-narrow py-12 text-muted">Laden…</div>

  return (
    <div className="mx-auto max-w-3xl px-5 py-8 space-y-5">
      <h1 className="font-display text-4xl sm:text-6xl tracking-display uppercase mb-1">Mijn inschrijvingen</h1>

      {error && (
        <p className="text-base text-muted">
          We konden je gegevens niet ophalen. Probeer de pagina opnieuw te laden.
        </p>
      )}

      {!error && history.length === 0 && (
        <div className="card p-6 shadow-block">
          <p className="text-lg">
            We hebben van jou nog geen afgesloten editie bewaard. Zodra een editie afgerond is,
            vind je ze hier terug.
          </p>
        </div>
      )}

      {history.map((h, i) => (
        // Een gedeelde brievenbus (zie CLAUDE.md, "Personen / Inschrijvingen")
        // kan twee verschillende mensen dezelfde editie geven — h.year alleen
        // is dus geen unieke key meer.
        <div key={`${h.year}-${i}`} className="card p-5 shadow-block">
          <div className="font-display text-4xl tracking-display uppercase mb-2">
            Rommelmarkt {h.year}
          </div>
          {/* De marktdag van díé editie, van de rij zelf — niet uit `season`,
              dat wijst naar de editie die nu loopt. */}
          {h.eventDate && (
            <p className="text-sm text-muted -mt-1 mb-2">{nlDatum(h.eventDate)}</p>
          )}
          <dl className="space-y-1 text-base">
            <Row label="Op naam van">{h.naam}</Row>
            <Row label={h.aantalPlaatsen === 1 ? 'Plek' : 'Plekken'}>
              {h.spotLabels.length > 0
                ? h.spotLabels.join(', ')
                : h.wachtlijst
                  ? 'Je stond op de wachtlijst'
                  : 'Geen plek toegewezen'}
            </Row>
            <Row label="Aantal standjes">{h.aantalPlaatsen}</Row>
            <Row label="Betaald">{h.betaald ? 'Ja' : 'Niet geregistreerd'}</Row>
            {h.inschrijvingsdatum && (
              <Row label="Ingeschreven op">{longDate(h.inschrijvingsdatum)}</Row>
            )}
          </dl>
        </div>
      ))}

      <div className="flex flex-col sm:flex-row gap-3 sm:items-center pt-2">
        <Link to={`/mijn-plek/${encodeURIComponent(token)}${verify ? `?v=${encodeURIComponent(verify)}` : ''}`}>
          <BigButton variant="ghost">← Terug naar mijn pagina</BigButton>
        </Link>
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="font-display tracking-display uppercase text-muted">{label}:</dt>
      <dd>{children}</dd>
    </div>
  )
}
